import {
  Button,
  type ButtonProps,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiXCircle } from "react-icons/fi";
import {
  BidV2Status,
  type BuyerBidV2DetailDto,
  useGetBidV2RejectionDocumentsQuery,
  useRejectBidV2Mutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import {
  type RejectionSubmission,
  RejectSupplierModal,
} from "../../../../common/rejection/RejectSupplierModal";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import { RejectedSummary } from "./BidRejectionSummary";

interface Props {
  detail: BuyerBidV2DetailDto;
  onViewRejectionDetails?: () => void;
}

export const BidRejectionActionCard = ({
  detail,
  onViewRejectionDetails,
}: Props) => {
  if (detail.bid.status === BidV2Status.Rejected) {
    return (
      <RejectedSummary detail={detail} onViewDetails={onViewRejectionDetails} />
    );
  }
  if (detail.bid.status !== BidV2Status.Submitted) {
    return null;
  }
  return <RejectAction detail={detail} />;
};

const RejectAction = ({ detail }: Props) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={FiXCircle} title={t("Actions")}>
      <RejectBidButton detail={detail} />
    </SectionCard>
  );
};

export const RejectBidButton = ({
  detail,
  size = "md",
}: {
  detail: BuyerBidV2DetailDto;
  size?: ButtonProps["size"];
}) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        size={size}
        colorScheme="red"
        variant="outline"
        width="full"
        onClick={disclosure.onOpen}
      >
        {t("Avvis leverandør")}
      </Button>
      {disclosure.isOpen ? (
        <RejectBidModal
          detail={detail}
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
        />
      ) : null}
    </>
  );
};

const RejectBidModal = ({
  detail,
  isOpen,
  onClose,
}: {
  detail: BuyerBidV2DetailDto;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { triggerSoft } = useApiError();
  const [rejectBidV2, rejectState] = useRejectBidV2Mutation();
  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const { data, refetch } = useGetBidV2RejectionDocumentsQuery({
    eventId,
    bidId,
  });

  const submit = async (submission: RejectionSubmission) => {
    try {
      await rejectBidV2({
        eventId,
        bidId,
        rejectBidV2RequestDto: submission,
      }).unwrap();
      onClose();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <RejectSupplierModal
      isOpen={isOpen}
      onClose={onClose}
      supplierName={detail.ownerName}
      organizationNumber={detail.ownerOrganizationNumber}
      foaPart={detail.foaPart}
      attachments={{
        entityType: "BidV2Rejection",
        entityId: bidId,
        eventId,
        documents: data?.documents ?? [],
        refetch,
      }}
      onSubmit={submit}
      isSubmitting={rejectState.isLoading}
    />
  );
};
